// A device model's product sheet (ADR-0478, #2026): what the manufacturer's
// own document publishes about the box — its interfaces, its wireless, its
// power, how it mounts — with a link to that document, and for a switch the
// firmware builds arpIQ manages. Drawn from the catalogue entry and from
// nothing else, so the Device models page and a device's page say the same
// thing from the one read each already makes (decision 7a).
//
// **A fact the document does not state reads "Not stated"** (decision 3),
// never a zero or a dash that a seller could read as "none". A model nobody
// has read a document for says so once, rather than a wall of Not stated.
//
// **No wording calls a datasheet fact tested, measured or guaranteed**
// (decision 8): each block is headed by what it is and named by its source,
// with no qualifier (8a, as ADR-0432 4c ruled for speeds). No ADR number,
// path or schema term is copy here (#362).
import type { components } from "../../api/schema";
import { plural } from "../../dashboard/format";
import type { DeviceModel } from "./DeviceDetail";
import { wordFor } from "../../api/openEnum";

type Interface = components["schemas"]["DeviceModelInterface"];
type Band = components["schemas"]["DeviceModelRadioBand"];
type Support = components["schemas"]["DeviceModelSupport"];

/** Restates ADR-0478 decision 2a — the generation with its amendment, so a
 * buyer who knows one name reads the other. */
const GENERATION: Record<string, string> = {
  wifi4: "Wi-Fi 4 (802.11n)", wifi5: "Wi-Fi 5 (802.11ac)", wifi6: "Wi-Fi 6 (802.11ax)",
  wifi6e: "Wi-Fi 6E (802.11ax, 6 GHz)", wifi7: "Wi-Fi 7 (802.11be)",
};
const KIND: Record<string, string> = { copper: "RJ-45", sfp: "SFP", qsfp: "QSFP", console: "Console", usb: "USB" };
const POE: Record<string, string> = { "802.3af": "PoE (802.3af)", "802.3at": "PoE+ (802.3at)", "802.3bt": "PoE++ (802.3bt)" };
const IOT: Record<string, string> = { ble: "Bluetooth LE", zigbee: "Zigbee", thread: "Thread" };
const MOUNT: Record<string, string> = { ceiling: "Ceiling", wall: "Wall", wall_box: "Wall box", rack: "Rack", pole: "Pole", desk: "Desk" };

/** A speed in Mb/s, the short way past a thousand: 2500 is "2.5 Gb/s". */
function speed(mbps: number): string {
  return mbps >= 1000 ? `${Number((mbps / 1000).toFixed(1))} Gb/s` : `${mbps} Mb/s`;
}

/** A figure as the document prints it: never rounded past three decimals, so 1.725 kg stays 1.725. */
function num(v: number): string {
  return Number(v.toFixed(3)).toLocaleString("en-US", { maximumFractionDigits: 3 });
}

/** One interface row as a seller says it: "8 × RJ-45 · 2.5 Gb/s · PoE++ (802.3bt)". */
export function interfaceWords(i: Interface): string {
  const parts = [`${i.count} × ${wordFor(KIND, i.kind)}`];
  if (i.max_speed_mbps) parts.push(speed(i.max_speed_mbps));
  if (i.poe) parts.push(wordFor(POE, i.poe));
  return parts.join(" · ");
}

/** One radio: "5 GHz · 4 streams · up to 2,400 Mb/s". The rate stays in the Mb/s the document
 * prints it in: 2,882 Mb/s said as 2.9 Gb/s would overstate the manufacturer's own figure. */
export function bandWords(b: Band): string {
  const parts = [`${b.band} GHz`];
  if (b.spatial_streams) parts.push(`${b.spatial_streams} ${plural(b.spatial_streams, "stream")}`);
  if (b.max_phy_rate_mbps) parts.push(`up to ${num(b.max_phy_rate_mbps)} Mb/s`);
  return parts.join(" · ");
}

export function dimensionsWords(d: components["schemas"]["DeviceModelDimensions"]): string | undefined {
  if (d.diameter !== undefined && d.h !== undefined) return `⌀ ${num(d.diameter)} × ${num(d.h)} mm`;
  if (d.w !== undefined && d.d !== undefined && d.h !== undefined) return `${num(d.w)} × ${num(d.d)} × ${num(d.h)} mm (W × D × H)`;
  return undefined;
}

function weightWords(g: number): string {
  return g >= 1000 ? `${num(g / 1000)} kg` : `${num(g)} g`;
}

/** Whether the catalogue holds anything a sheet can show. */
export function hasSheet(model: DeviceModel): boolean {
  return !!model.datasheet || model.support !== undefined;
}

/** Whether a row was given anything to say: `cond && value` leaves `false` or `undefined` behind. */
function present(c: React.ReactNode): boolean {
  return Array.isArray(c) ? c.some(present) : c !== undefined && c !== null && c !== false && c !== "";
}

/** One row: the value, or "Not stated" where the document states none. */
function Row({ k, children }: { k: string; children?: React.ReactNode }) {
  const stated = present(children);
  return (
    <div className="sheet-row">
      <dt>{k}</dt>
      {/* Restates ADR-0478 decisions 3 and 8a — a fact the source does not state is absent, and is said as such. */}
      <dd>{stated ? children : <span className="muted">Not stated</span>}</dd>
    </div>
  );
}

function Group({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section className="sheet-group" aria-label={heading}>
      <h4>{heading}</h4>
      <dl>{children}</dl>
    </section>
  );
}

/** Where the facts come from, as a link a seller can open in front of the customer (ADR-0478 decision 4a). */
function Source({ d }: { d: NonNullable<DeviceModel["datasheet"]> }) {
  const label = [d.title, d.revision, d.published].filter(Boolean).join(" · ");
  return (
    <p className="sheet-source">
      {/* Restates ADR-0478 decisions 4a and 8a — named by its source, with no qualifier. */}
      From the manufacturer&rsquo;s datasheet: <a href={d.url} target="_blank" rel="noreferrer noopener">{label}</a>
    </p>
  );
}

/** The builds arpIQ manages for a switch model (ADR-0478 decision 6a), each with what it does there. */
function Managed({ support }: { support: Support[] }) {
  return (
    <section className="sheet-managed" aria-label="Managed by arpIQ">
      <h4>Managed by arpIQ</h4>
      {support.length === 0
        // Restates ADR-0478 decision 6a — a build is listed only where arpIQ's own switch tables admit it for this model.
        ? <p className="muted">No firmware build of this model is listed as managed yet.</p>
        : (
          <ul>
            {support.map((s) => (
              <li key={s.build}>
                <code>{s.build}</code>
                {s.console_read && <span className="dim"> · running configuration read</span>}
                {s.native_settings.length > 0 && (
                  <span className="dim" title={s.native_settings.join(", ")}> · {s.native_settings.length} {plural(s.native_settings.length, "console setting")}</span>
                )}
              </li>
            ))}
          </ul>
        )}
    </section>
  );
}

/** The sheet. `compact` lays the groups in one column, for a tile. */
export default function ModelSheet({ model, compact = false }: { model: DeviceModel; compact?: boolean }) {
  const ap = model.family === "ap";
  const r = model.radio, p = model.power, ph = model.physical;
  return (
    <div className={"model-sheet" + (compact ? " is-compact" : "")}>
      {model.datasheet
        ? (
          <>
            <Source d={model.datasheet} />
            <div className="sheet-groups">
              <Group heading="Interfaces">
                <Row k="Ports">{model.interfaces && model.interfaces.length > 0 && <ul className="sheet-list">{model.interfaces.map((i, n) => <li key={n}>{interfaceWords(i)}</li>)}</ul>}</Row>
              </Group>
              {ap && (
                <Group heading="Wireless">
                  <Row k="Generation">{r?.wifi_generation && GENERATION[r.wifi_generation]}</Row>
                  <Row k="Radios">{r?.radios && r.radios.length > 0 && <ul className="sheet-list">{r.radios.map((b, n) => <li key={n}>{bandWords(b)}</li>)}</ul>}</Row>
                  <Row k="IoT radios">{r?.iot_radios && r.iot_radios.length > 0 && r.iot_radios.map((v) => IOT[v] ?? v).join(", ")}</Row>
                </Group>
              )}
              <Group heading="Power">
                <Row k="Maximum draw">{p?.max_draw_w !== undefined && `${num(p.max_draw_w)} W`}</Row>
                {ap
                  ? (
                    <>
                      <Row k="PoE input">{p?.poe_in && (wordFor(POE, p.poe_in))}</Row>
                      <Row k="DC input">{p?.dc_in !== undefined && (p.dc_in ? "Yes" : "No")}</Row>
                    </>
                  )
                  : <Row k="PoE budget">{p?.poe_budget_w !== undefined && `${num(p.poe_budget_w)} W`}</Row>}
              </Group>
              <Group heading="Mounting and enclosure">
                <Row k="Mounting">{ph?.mounts && ph.mounts.length > 0 && ph.mounts.map((v) => MOUNT[v] ?? v).join(", ")}</Row>
                <Row k="Enclosure">{ph?.enclosure && (ph.enclosure === "indoor" ? "Indoor" : "Outdoor")}{ph?.ip_rating && <>{ph.enclosure ? " · " : ""}{ph.ip_rating}</>}</Row>
                <Row k="Dimensions">{ph?.dimensions_mm && dimensionsWords(ph.dimensions_mm)}</Row>
                <Row k="Weight">{ph?.weight_g !== undefined && weightWords(ph.weight_g)}</Row>
                <Row k="Operating range">{ph?.operating_temp_c && `${num(ph.operating_temp_c.min)} to ${num(ph.operating_temp_c.max)} °C`}</Row>
                {ap && <Row k="Antennas">{ph?.antennas && (ph.antennas === "internal" ? "Internal" : "External")}</Row>}
              </Group>
            </div>
          </>
        )
        // Restates ADR-0478 decision 3a — only the manufacturer's own document is a source, and a model without one shows none.
        : <p className="muted sheet-none">No manufacturer&rsquo;s datasheet has been read for this model.</p>}
      {model.support !== undefined && <Managed support={model.support} />}
    </div>
  );
}
